/**
 * Página de Puntos de Embarque — moderna.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Box, Typography, Button, IconButton, TextField, Switch, Tooltip, Avatar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AnchorIcon from '@mui/icons-material/Anchor';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } };
const getAuthHeaders = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

const emptyForm = { nombre: '', direccion: '', instrucciones_llegada: '' };

export default function PuntosEmbarquePage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActivo, setFilterActivo] = useState('');
  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/puntos-embarque?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() });
      setItems(await r.json());
    } catch { toast.error('Error al cargar'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (q && !(i.nombre || '').toLowerCase().includes(q) && !(i.direccion || '').toLowerCase().includes(q)) return false;
      if (filterActivo === 'activos' && !i.activo) return false;
      if (filterActivo === 'inactivos' && i.activo) return false;
      return true;
    });
  }, [items, search, filterActivo]);

  const handleNew = () => { setEditItem(null); setForm(emptyForm); setSlideOpen(true); };
  const handleEdit = (r) => { setEditItem(r); setForm({ nombre: r.nombre, direccion: r.direccion || '', instrucciones_llegada: r.instrucciones_llegada || '' }); setSlideOpen(true); };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta nombre');
    setSaving(true);
    try {
      const body = { ...form };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem ? `${API_BASE}/turismo/puntos-embarque/${editItem.id}` : `${API_BASE}/turismo/puntos-embarque`;
      const r = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success(editItem ? 'Punto actualizado' : 'Punto creado');
      setSlideOpen(false); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleToggle = async (row, v) => {
    setItems(xs => xs.map(x => x.id === row.id ? { ...x, activo: v } : x));
    try {
      const r = await fetch(`${API_BASE}/turismo/puntos-embarque/${row.id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo: v }) });
      if (!r.ok) throw new Error();
      toast.success(v ? 'Activado' : 'Desactivado'); refreshCounts?.();
    } catch { setItems(xs => xs.map(x => x.id === row.id ? { ...x, activo: !v } : x)); toast.error('Error'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/puntos-embarque/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!r.ok) throw new Error('No se pudo eliminar');
      toast.success('Eliminado'); setDeleteTarget(null); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  const handleBulkDelete = async () => {
    if (!deletingBulk) return;
    try {
      await Promise.all(deletingBulk.ids.map(id => fetch(`${API_BASE}/turismo/puntos-embarque/${id}`, { method: 'DELETE', headers: getAuthHeaders() })));
      toast.success(`${deletingBulk.ids.length} eliminado(s)`); deletingBulk.run(); setDeletingBulk(null); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const handleBulkToggle = async (ids, activo, clear) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API_BASE}/turismo/puntos-embarque/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo }) })));
      toast.success(`${ids.length} actualizado(s)`); clear(); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const columns = [
    { key: 'nombre', label: 'Punto de embarque', sortable: true,
      render: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#ec489915', color: '#ec4899', width: 32, height: 32 }}><AnchorIcon sx={{ fontSize: 18 }} /></Avatar>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{r.nombre}</Typography>
        </Box>
      ) },
    { key: 'direccion', label: 'Dirección', sortable: true,
      render: r => <Typography sx={{ fontSize: 12, color: '#475569' }} noWrap>{r.direccion || '—'}</Typography> },
    { key: 'instrucciones_llegada', label: 'Instrucciones',
      render: r => <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap title={r.instrucciones_llegada}>{r.instrucciones_llegada ? r.instrucciones_llegada.slice(0, 50) + (r.instrucciones_llegada.length > 50 ? '…' : '') : '—'}</Typography> },
    { key: 'activo', label: 'Activo', align: 'center', sortable: true,
      render: r => <Switch size="small" checked={!!r.activo} onClick={e => e.stopPropagation()} onChange={(_, v) => handleToggle(r, v)} /> },
    { key: 'acciones', label: '', align: 'right',
      render: r => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#ec4899' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(r)} sx={{ color: '#dc2626' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Box>
      ) },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Puntos de Embarque</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>Muelles desde donde salen los tours. Cada salida tiene un punto asignado.</Typography>
      </Box>

      <DataTable columns={columns} rows={filtered} loading={loading} rowKey="id" onRowClick={handleEdit}
        search={{ value: search, onChange: setSearch, placeholder: 'Buscar...' }}
        filters={[
          { key: 'estado', label: 'Estado', value: filterActivo, onChange: setFilterActivo,
            options: [{ value: 'activos', label: 'Solo activos' }, { value: 'inactivos', label: 'Solo inactivos' }] },
        ]}
        bulkActions={[
          { label: 'Activar', icon: <VisibilityIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, true, c) },
          { label: 'Desactivar', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, false, c) },
          { label: 'Eliminar', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => setDeletingBulk({ ids, run: c }) },
        ]}
        actions={<Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleNew}
          sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>Nuevo punto</Button>}
        emptyState={items.length === 0
          ? <EmptyState icon={<AnchorIcon />} title="Sin puntos de embarque" description="Registra los muelles desde donde salen tus tours." ctaLabel="Crear" onCta={handleNew} />
          : <EmptyState icon={<AnchorIcon />} title="Sin resultados" size="sm" />}
      />

      <SlideOver open={slideOpen} onClose={() => setSlideOpen(false)}
        title={editItem ? 'Editar Punto' : 'Nuevo Punto'} icon={<AnchorIcon />}
        subtitle={editItem?.nombre || 'Muelle de embarque'}
        footer={<>
          <Button onClick={() => setSlideOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)' }}>
            {saving ? 'Guardando...' : (editItem ? 'Guardar' : 'Crear')}
          </Button>
        </>}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Nombre *" size="small" fullWidth value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
            placeholder="Ej: Muelle de la Bodeguita" />
          <TextField label="Dirección" size="small" fullWidth value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} />
          <TextField label="Instrucciones de llegada" size="small" fullWidth multiline rows={4} value={form.instrucciones_llegada}
            onChange={e => setForm({ ...form, instrucciones_llegada: e.target.value })}
            placeholder="Cómo llegar, qué buscar, contacto local, etc." />
        </Box>
      </SlideOver>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="¿Eliminar este punto?" message={deleteTarget ? `Vas a eliminar "${deleteTarget.nombre}".` : ''} confirmText="Eliminar" />
      <ConfirmDialog open={!!deletingBulk} onClose={() => setDeletingBulk(null)} onConfirm={handleBulkDelete}
        title={`¿Eliminar ${deletingBulk?.ids?.length || 0}?`} message="Esta acción no se puede deshacer." confirmText="Eliminar todos" />
      <ToastContainer />
    </Box>
  );
}
